import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Plotly from "plotly.js/dist/plotly";

import { DIALOG_TYPES } from "../../../packages/ConfirmationPopup/index.js";
import ConfirmationPopup from "../../../packages/ConfirmationPopup/index.js";
import CustomModal from "../../../packages/CustomModal";
import { FEEDBACK_TYPES } from "../../../packages/DPFeedbackIconTooltip";
import IconButton from "../../../packages/IconButton";
import { AccordionDownIcon } from "../../../packages/SVGs";
import WPAgGrid from "../../../packages/WPAgGrid/WPAgGrid";
import WPButton from "../../../packages/WPButton/index.jsx";
import { WPDropDownMenu } from "../../../packages/WPDropDownMenu/WPDropDownMenu.jsx";
import { WPRangedInput } from "../../../packages/WPInput/WPRangedInput";
import WPLoader from "../../../packages/WPLoader/WPLoader.jsx";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";

import DupontCheckbox from "../../../components/DupontCheckbox/index.js";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import StatusApiErrorDialog from "../../../components/StatusAPIErrorDialog/index.jsx";
import exportAllReplacementTables from "../../../XLSDownload/ExportAllTables/raTableExport";
import { ADVANCED_SETTINGS_SECTION_KEYS } from "../../analyseData/AdvancedSettings/advancedSettings.constants";
import ReplacementResultChart from "../charts";
import ExportAllData from "./exportAllData";
import { RELACEMENT_ANALYSIS_COL_DEF } from "./replacementAnalysisTable.config";

import {
  CHART_EXPORT_CONFIG,
  CustomModalButtons,
  FORECAST_WINDOW,
  MODELING_UI_TEXT_KEYS,
  PLOTLY_CLASS_NAME,
  STATUS_API_ERROR_KEYS,
} from "../../../constants/global.constants";
import {
  LICENSE_PERMISSION_IDS,
  LICENSE_STATUS,
} from "../../../constants/LicenseManagement.constants.js";

import { QuestionIcon } from "../../../assets/icons";

import { createDownloadMenuItems } from "../../../common/configs/downloadMenuConfig";

import { getAIRecommendationFeedback } from "../../../utils/aiRecommendationFeedback";
import { calculateTableHeight } from "../../../utils/tableDataFormatter.js";
import {
  fetchSuggestedSettings,
  formatSuggestedSettings,
  getPercentageLoaderMsg,
} from "../../../utils/Utils";
import { generateFileName } from "../../../utils/Utils.js";

import {
  getAnalysis,
  getAnalysisJob,
  getAnalysisStatus,
  getLastFileDetails,
  getLifetimeWordReport,
  getType,
} from "../../../network/cipAPI";

import {
  storeAnalysisData,
  storeReplacementForecastWindow,
  storeTaskId,
} from "../../../Store/TemplateStore/template.action";

import "./index.scss";

const ReplacementResult = ({
  setDisableNextButton,
  detailsResult,
  setAdvancedSettingsConfig,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const intervalIdRef = useRef(null);
  const chartRef = useRef(null);
  const replacementAnalysisGridRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState(null);
  const [taskId, setTaskId] = useState(useSelector((state) => state.taskId));
  const [rowData, setRowData] = useState(null);
  const [translatedRowData, setTranslatedRowData] = useState(null);
  const [isWordDownloading, setIsWordDownloading] = useState(false);
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [errorMsgFailedPrediction, setErrorMsgFailedPrediction] =
    useState(null);
  const [recommendedCipsFlag, setRecommendedCipsFlag] = useState(0);

  const selectedTrain = useSelector((state) => state.selTrain);
  const installationDate = useSelector((state) => state.installation_date);
  const userLicenses = useSelector((state) => state.userLicenses || []);

  const handleUseRecommendedCipsChange = (isChecked) => {
    setRecommendedCipsFlag(isChecked ? 1 : 0);
  };

  const downloadFileName = useMemo(
    () =>
      generateFileName(
        t("replacementAnalysisStrings.replacementAdvisorReport"),
      ),
    [t],
  );

  const allData = ExportAllData();
  const [exportAllData, setExportAllData] = useState({
    ...allData,
    replacementAnalysisData: rowData,
    replacementPredictionData: plotData,
  });
  const storedForecastYears = useSelector((state) => state.forecast_window);
  const [forecastYears, setForecastYears] = useState(storedForecastYears);
  const [lastAnalyzedForecast, setLastAnalyzedForecast] = useState(null);
  const [lastAnalyzedRecommendedCipsFlag, setLastAnalyzedRecommendedCipsFlag] =
    useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isNoStablePointsDialogOpen, setIsNoStablePointsDialogOpen] =
    useState(false);
  const [isNoPredictedCipsDialogOpen, setIsNoPredictedCipsDialogOpen] =
    useState(false);
  const [showWordReportConfirmation, setShowWordReportConfirmation] =
    useState(false);
  const [sameCIPUITextkey, setSameCIPUITextkey] = useState("");
  const colDef = RELACEMENT_ANALYSIS_COL_DEF(t, translatedRowData);
  const shouldShowSameClPInfoBanner =
    sameCIPUITextkey === MODELING_UI_TEXT_KEYS.PREDICTED_RECOMMENDED_CIPS_SAME;

  const isAnalysisInputUnchanged =
    forecastYears === lastAnalyzedForecast &&
    recommendedCipsFlag === lastAnalyzedRecommendedCipsFlag;

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const onHelpBtnClick = () => {
    setHelpDialogOpen(true);
  };

  const handleAdvanceSettingsSave = () => {
    // Trigger the analysis API call again when advance settings are saved only if there is a stored forecast window(already analysed window)
    if (storedForecastYears) {
      setForecastYears(storedForecastYears); //reset the forecast years to the stored value if user chanes in input box but didn't run prediction
      onReplacementResultClicked(storedForecastYears);
    }
  };

  useEffect(() => {
    setAdvancedSettingsConfig?.({
      selectedTrain,
      enabledSections: [
        ADVANCED_SETTINGS_SECTION_KEYS.CIP_PREDICTION_AND_RECOMMENDATION,
        ADVANCED_SETTINGS_SECTION_KEYS.LIFETIME,
      ],
      onSave: handleAdvanceSettingsSave,
      excludeFields: ["max_evals", "optimization_target"],
    });
    return () => setAdvancedSettingsConfig?.(null);
  }, [selectedTrain, storedForecastYears]);

  useEffect(() => {
    setExportAllData({
      ...allData,
      replacementAnalysisData: rowData,
      replacementPredictionData: plotData,
    });
  }, [rowData, plotData, allData]);

  const handleForecastChange = (e) => {
    const value = parseInt(e?.target?.value ?? e) || null;
    setErrorMsg("");

    if (value && value >= FORECAST_WINDOW.MIN && value <= FORECAST_WINDOW.MAX) {
      setForecastYears(value);
      setExportAllData((prevState) => ({
        ...prevState,
        others: [
          {
            ...prevState.others[0],
            forecast_window: value,
          },
        ],
      }));
    } else {
      setForecastYears(value);
      setErrorMsg(t("replacementAnalysisStrings.forecastWindowError"));
    }
  };

  const checkStatus = async (task_id, derivedForecastYears) => {
    try {
      let id = task_id || taskId;
      let analysisStatus;
      if (id === "" || id === null || id === undefined) {
        analysisStatus = { data: { status: "pending" } };
      } else {
        analysisStatus = await getAnalysisStatus(id);
      }

      let { status, processed, error } = analysisStatus?.data || {};
      const isNoStablePointsError =
        error === STATUS_API_ERROR_KEYS.NO_STABLE_POINTS;
      const isNoPredictedCipsError =
        error === STATUS_API_ERROR_KEYS.NO_PREDICTED_CIPS;
      status = status?.toLowerCase();

      try {
        if (status === "completed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          const analysisResult = await getAnalysis(
            selectedTrain,
            derivedForecastYears,
            recommendedCipsFlag,
          );

          dispatch(storeReplacementForecastWindow(derivedForecastYears));
          setShowOverlayLoader(false);
          setIsNoStablePointsDialogOpen(false);
          setIsNoPredictedCipsDialogOpen(false);
          setIsLoading(false);
          setDisableNextButton(false);
          const modelingData = analysisResult?.data["modeling"];
          if (modelingData) {
            try {
              const parsedData = JSON.parse(modelingData);
              dispatch(storeAnalysisData(parsedData));
              setRowData(parsedData.replacement_results);
              setPlotData(parsedData.plot_results);
              setSameCIPUITextkey(parsedData?.ui_text || "");

              dispatch(storeTaskId(""));
              setTaskId("");
            } catch (error) {
              setSameCIPUITextkey("");
              createNotification(
                ERROR_NOTIFICATION,
                t("notificationStrings.somethingWrong"),
                "",
              );
            } finally {
              setDisableNextButton(false);
              setIsLoading(false);
              setLastAnalyzedForecast(derivedForecastYears);
              setLastAnalyzedRecommendedCipsFlag(recommendedCipsFlag);
            }
          }
        } else if (status === "running") {
          setprocessedPercentage(processed);
          setIsLoading(true);
          setDisableNextButton(true);
          setShowOverlayLoader(false);
        } else if (status === "failed") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setDisableNextButton(false);
          setShowOverlayLoader(false);

          if (isNoStablePointsError) {
            setIsNoStablePointsDialogOpen(true);
            setIsNoPredictedCipsDialogOpen(false);
            setErrorMsgFailedPrediction("");
          } else if (isNoPredictedCipsError) {
            setIsNoPredictedCipsDialogOpen(true);
            setIsNoStablePointsDialogOpen(false);
            setErrorMsgFailedPrediction("");
          } else {
            createNotification(
              ERROR_NOTIFICATION,
              error || t("predictionStrings.failedPrediction"),
              "",
            );
            setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
          }

          dispatch(storeTaskId(""));
          setTaskId("");
          setRowData(null);
          setPlotData(null);
          setSameCIPUITextkey("");
          dispatch(storeAnalysisData(null));
        } else if (status === "pending") {
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setIsLoading(false);
          setShowOverlayLoader(false);
          setDisableNextButton(false);
          setSameCIPUITextkey("");
        }
      } catch (error) {
        setShowOverlayLoader(false);
        setIsLoading(false);
        setDisableNextButton(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || error.response.status === 502) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.networkError"),
          "",
        );
      } else {
        setErrorMsgFailedPrediction(t("predictionStrings.errorPrediction"));
        setIsLoading(false);
        setDisableNextButton(false);
        setShowOverlayLoader(false);
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        dispatch(storeTaskId(""));
        setTaskId("");
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      }
    }
  };

  const onReplacementResultClicked = async (storedForecastYearsOverride) => {
    const derivedForecastYears = storedForecastYearsOverride || forecastYears;
    setIsLoading(true);
    setIsNoStablePointsDialogOpen(false);
    setIsNoPredictedCipsDialogOpen(false);
    setDisableNextButton(true);
    setprocessedPercentage("1%");
    setErrorMsgFailedPrediction(null);
    setSameCIPUITextkey("");

    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    try {
      const convertedType = await getType();
      setTranslatedRowData(convertedType.data);
      let response = await getAnalysisJob(
        derivedForecastYears,
        selectedTrain,
        recommendedCipsFlag,
      );

      if (response.status !== 200) {
        throw new Error(response);
      }

      const task_id = response?.data?.task_id;
      dispatch(storeTaskId(task_id));
      setTaskId(task_id);

      intervalIdRef.current = setInterval(function () {
        checkStatus(task_id, derivedForecastYears);
      }, 15000);
    } catch (error) {
      setIsLoading(false);
      setDisableNextButton(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  useEffect(() => {
    const fetchPreviousData = async () => {
      try {
        setIsLoading(true);
        setDisableNextButton(true);
        const convertedType = await getType();
        setTranslatedRowData(convertedType.data);

        // Fetch last file details to get previous_ra_prediction
        const fileDetails = await getLastFileDetails(selectedTrain);
        const { previous_ra_prediction } = fileDetails.data;
        if (previous_ra_prediction) {
          const { modeling, forecast_window, recommended_cips_flag } =
            previous_ra_prediction;

          if (modeling) {
            const parsedData = JSON.parse(modeling);
            dispatch(storeAnalysisData(parsedData));
            setRowData(parsedData.replacement_results);
            setPlotData(parsedData.plot_results);
            setSameCIPUITextkey(parsedData?.ui_text || "");

            if (forecast_window) {
              const roundedForecastWindow = Math.ceil(forecast_window);
              setForecastYears(roundedForecastWindow);
              setLastAnalyzedForecast(roundedForecastWindow);
              dispatch(storeReplacementForecastWindow(roundedForecastWindow));
            }
            if (recommended_cips_flag !== undefined) {
              setRecommendedCipsFlag(recommended_cips_flag);
              setLastAnalyzedRecommendedCipsFlag(recommended_cips_flag);
            }
          }
        } else {
          setForecastYears(null);
          dispatch(storeReplacementForecastWindow(null));
          dispatch(storeAnalysisData(null));
          setRowData(null);
          setPlotData(null);
          setSameCIPUITextkey("");
        }

        // Clear any existing taskId to allow new analysis
        dispatch(storeTaskId(""));
        setTaskId("");
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setIsLoading(false);
        setDisableNextButton(false);
      }
    };

    // Only fetch data when detailsResult becomes true (after PUT API completes)
    if (detailsResult) {
      fetchPreviousData();
    } else {
      // If detailsResult is false/undefined, ensure loading state is turned off
      setIsLoading(false);
      setDisableNextButton(false);
      // Clear any existing taskId
      dispatch(storeTaskId(""));
      setTaskId("");
    }

    // Cleanup function when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [detailsResult]);

  const handleExcelExport = async () => {
    try {
      setIsExcelDownloading(true);
      const suggestedSettings = await fetchSuggestedSettings(
        selectedTrain,
        dispatch,
      );

      const suggestedSettingsForExport = suggestedSettings
        ? formatSuggestedSettings(suggestedSettings, t)
        : null;

      const exportDataWithSuggested = {
        ...exportAllData,
        suggestedOthers: suggestedSettingsForExport,
        // Keep export aligned with last analyzed toggle state.
        recommendedCipsFlag: lastAnalyzedRecommendedCipsFlag,
      };

      await exportAllReplacementTables(
        downloadFileName,
        exportDataWithSuggested,
        translatedRowData,
        t,
        selectedTrain,
      );
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsExcelDownloading(false);
    }
  };

  // Converts Plotly chart to base64 PNG for Word export
  //Temporarily resize chart → capture image → restore original layout
  const convertChartToBase64 = async (
    plotlyDiv,
    exportHeight,
    exportMarginTop,
    exportMarginBottom,
  ) => {
    const rect = plotlyDiv?.getBoundingClientRect();
    const currentWidth = Math.round(rect?.width || plotlyDiv?.clientWidth || 0);
    const currentHeight = Math.round(
      rect?.height || plotlyDiv?.clientHeight || 0,
    );
    let originalLayout = null;

    try {
      if (Plotly?.toImage && currentWidth > 0 && currentHeight > 0) {
        // Store original layout for restoration
        const { margin, xaxis, yaxis } = plotlyDiv?.layout || {};
        originalLayout = {
          width: currentWidth,
          height: currentHeight,
          "margin.b": margin?.b,
          "margin.t": margin?.t,
          "xaxis.automargin": xaxis?.automargin,
          "yaxis.automargin": yaxis?.automargin,
        };

        // Temporarily resize chart with explicit margins
        await Plotly.relayout(plotlyDiv, {
          width: currentWidth,
          height: exportHeight,
          "margin.b": exportMarginBottom,
          "margin.t": exportMarginTop,
          "xaxis.automargin": false,
          "yaxis.automargin": false,
        });
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const exportedImage = await Plotly.toImage(plotlyDiv, {
          format: "png",
          width: currentWidth,
          height: exportHeight,
          scale: 1,
        });
        if (exportedImage) {
          return exportedImage.replace(/^data:image\/png;base64,/, "");
        }
      }
      return null;
    } catch {
      return null;
    } finally {
      // Restore original layout
      if (originalLayout) {
        await Plotly.relayout(plotlyDiv, originalLayout);
      }
    }
  };

  const handleWordDownload = async () => {
    try {
      setIsWordDownloading(true);
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Chart export dimensions from global constants
      const { PLOT_AREA_HEIGHT, MARGIN_TOP, MARGIN_BOTTOM } =
        CHART_EXPORT_CONFIG;
      const exportHeight = PLOT_AREA_HEIGHT + MARGIN_TOP + MARGIN_BOTTOM;
      // Get all Plotly chart divs from the chart container
      const plotlyDivs =
        chartRef.current?.querySelectorAll(PLOTLY_CLASS_NAME.CLASS_NAME) || [];

      // Convert all charts to base64 in parallel
      const chartConversion = Array.from(plotlyDivs).map((plotlyDiv) =>
        convertChartToBase64(
          plotlyDiv,
          exportHeight,
          MARGIN_TOP,
          MARGIN_BOTTOM,
        ),
      );

      const charts = (await Promise.all(chartConversion)).filter(Boolean);
      const payload = {
        charts,
        replacement_results: rowData || [],
        forecast_window: forecastYears,
        installation_date: installationDate,
        recommended_cips_flag: lastAnalyzedRecommendedCipsFlag,
      };

      // Call API
      const response = await getLifetimeWordReport(selectedTrain, payload);

      if (response && response.status === 200) {
        const blob = response.data;
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${downloadFileName}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);
        }, 100);
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("predictionStrings.wordReportDownloadFailed"),
        "",
      );
    } finally {
      setIsWordDownloading(false);
    }
  };

  const handleWordReportClick = () => {
    if (aiRecommendationFeedback.requiresConfirmation) {
      setShowWordReportConfirmation(true);
      return;
    }

    handleWordDownload();
  };

  const handleWordReportConfirmationClose = (buttonId) => {
    setShowWordReportConfirmation(false);

    if (buttonId === CustomModalButtons.CONFIRM) {
      handleWordDownload();
    }
  };

  const aiRecommendationFeedback = useMemo(() => {
    return getAIRecommendationFeedback({
      userLicenses,
      licensePermissionId: LICENSE_PERMISSION_IDS.AI_RECOMMENDATIONS,
      licenseStatus: LICENSE_STATUS,
      feedbackTypes: FEEDBACK_TYPES,
      dialogTypes: DIALOG_TYPES,
    });
  }, [userLicenses]);

  const downloadMenuItems = createDownloadMenuItems(
    t,
    rowData !== null,
    isLoading,
    handleExcelExport,
    handleWordReportClick,
    aiRecommendationFeedback,
  );

  return (
    <div className="ag-theme-alpine container-padding-ra">
      {showOverlayLoader && <WPLoader />}
      <div className="descriptionText uploadContainer">
        {t("replacementAnalysisStrings.forecastDescription")}
      </div>
      <div className="flex-center-center">
        <div className="fitContent">
          <WPRangedInput
            value={forecastYears}
            label={t("replacementAnalysisStrings.forecastWindow")}
            onChange={handleForecastChange}
            ranges={{
              hard: { min: FORECAST_WINDOW.MIN, max: FORECAST_WINDOW.MAX },
            }}
            integer
            disabled={isLoading}
          />
        </div>
        <DupontCheckbox
          labelText={t("replacementAnalysisStrings.useRecommendedCips")}
          checked={recommendedCipsFlag === 1}
          onChange={handleUseRecommendedCipsChange}
          customClass="recommended-cips-checkbox"
          disabled={isLoading}
        />
        <div className="info-section">
          <WPButton
            onClick={() => onReplacementResultClicked()}
            customClass="margin-left-10"
            disabled={
              !forecastYears ||
              isLoading ||
              errorMsg ||
              isAnalysisInputUnchanged
            }
          >
            {t("replacementAnalysisStrings.runReplacementAnalysis")}
          </WPButton>
          <IconButton onClick={onHelpBtnClick}>
            <QuestionIcon />
          </IconButton>
        </div>
        <div className={`text-right margin-left`}>
          <WPDropDownMenu
            toggle={
              <div className="predict-download-toggle">
                {t("predictionStrings.downloadReport")}
                <AccordionDownIcon />
              </div>
            }
            disabled={rowData === null || isLoading}
            customClass="predict-dropdown-tertiary"
            menuItems={downloadMenuItems}
          />
        </div>
      </div>
      {isWordDownloading && (
        <WPLoader loadingMsg={t("predictionStrings.generatingWordReport")} />
      )}
      {isExcelDownloading && (
        <WPLoader loadingMsg={t("predictionStrings.generatingExcelReport")} />
      )}
      {isLoading ? (
        <WPLoader loadingMsg={getPercentageLoaderMsg(t, processedPercentage)} />
      ) : (
        <div>
          {errorMsgFailedPrediction && (
            <div className="errorMsg">{errorMsgFailedPrediction}</div>
          )}

          {rowData && shouldShowSameClPInfoBanner && (
            <WPMessageBanner
              type={BANNER_TYPES.info}
              message={t(
                "replacementAnalysisStrings.predictedRecommendedCipsSameInfo",
              )}
            />
          )}

          {rowData && (
            <div className="ro-card-wrapper" id="lifetime-analysis-table">
              <div className="ro-card-header">
                <div className="teal-heading">
                  {t("replacementAnalysisStrings.lifetimeTableTitle")}
                </div>
              </div>
              <div className="ro-ag-table">
                <WPAgGrid
                  gridRef={replacementAnalysisGridRef}
                  rowData={rowData}
                  columnDefs={colDef}
                  tableHeight={calculateTableHeight(rowData)}
                  exportFileName={t(
                    "replacementAnalysisStrings.lifetimeTableTitle",
                  )}
                />
              </div>
            </div>
          )}
          {plotData && (
            <div className="margin-top-20" ref={chartRef}>
              <div className="descriptionText marginBtm10">
                {t("replacementAnalysisStrings.chartDescription")}
              </div>
              <ReplacementResultChart plotData={plotData} />
            </div>
          )}

          {rowData && (
            <div className="ro-disclaimer">
              {t("disclaimer", { currentYear })}
            </div>
          )}
        </div>
      )}
      {helpDialogOpen && (
        <CustomModal
          show={helpDialogOpen}
          confirmBtn={t("ValueStrings.okay")}
          width="575px"
          size="small"
          header={t("helpInformation.predictionHelp")}
          onModalClose={() => setHelpDialogOpen(false)}
        >
          <div className="help-dialog-content">
            <div className="help-section">
              <ul className="help-list">
                <li className="help-list-item">
                  {t("helpInformation.predictionHelpModifyThreshold")}
                </li>
                <li className="help-list-item">
                  {t("helpInformation.predictionHelpAlterSettings")}
                </li>
              </ul>
            </div>
          </div>
        </CustomModal>
      )}

      {showWordReportConfirmation && (
        <ConfirmationPopup
          open={showWordReportConfirmation}
          type={aiRecommendationFeedback.dialogType}
          header={t(aiRecommendationFeedback.headerKey)}
          description={t(aiRecommendationFeedback.descriptionKey)}
          onClose={handleWordReportConfirmationClose}
          cancelBtn={t("ValueStrings.cancel")}
          confirmBtn={t("ValueStrings.export")}
          customBackdrop={true}
        ></ConfirmationPopup>
      )}

      {(isNoStablePointsDialogOpen || isNoPredictedCipsDialogOpen) && (
        <StatusApiErrorDialog
          show={isNoStablePointsDialogOpen || isNoPredictedCipsDialogOpen}
          dialogType={
            isNoPredictedCipsDialogOpen
              ? STATUS_API_ERROR_KEYS.NO_PREDICTED_CIPS
              : STATUS_API_ERROR_KEYS.NO_STABLE_POINTS
          }
          isLifetime
          onClose={() => {
            setIsNoStablePointsDialogOpen(false);
            setIsNoPredictedCipsDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ReplacementResult;
